import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Video,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Trash2
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import useConfirm from '../../hooks/useConfirm';
import ConfirmationModal from '../modals/ConfirmationModal';
import TimezoneSelector from './TimezoneSelector';
import { appointmentsAPI, handleApiError } from '../../utils/api';

// SVG Icons for video conference providers - Same as homepage for consistency
const GoogleMeetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-12 h-12" viewBox="0 0 48 48">
    <rect width="16" height="16" x="12" y="16" fill="#fff" transform="rotate(-90 20 24)"></rect>
    <polygon fill="#1e88e5" points="3,17 3,31 8,32 13,31 13,17 8,16"></polygon>
    <path fill="#4caf50" d="M37,24v14c0,1.657-1.343,3-3,3H13l-1-5l1-5h14v-7l5-1L37,24z"></path>
    <path fill="#fbc02d" d="M37,10v14H27v-7H13l-1-5l1-5h21C35.657,7,37,8.343,37,10z"></path>
    <path fill="#1565c0" d="M13,31v10H6c-1.657,0-3-1.343-3-3v-7H13z"></path>
    <polygon fill="#e53935" points="13,7 13,17 3,17"></polygon>
    <polygon fill="#2e7d32" points="38,24 37,32.45 27,24 37,15.55"></polygon>
    <path fill="#4caf50" d="M46,10.11v27.78c0,0.84-0.98,1.31-1.63,0.78L37,32.45v-16.9l7.37-6.22C45.02,8.8,46,9.27,46,10.11z"></path>
  </svg>
);

const ZoomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-12 h-12" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="20" fill="#2196f3"></circle>
    <path fill="#fff" d="M29,31H14c-1.657,0-3-1.343-3-3V17h15c1.657,0,3,1.343,3,3V31z"></path>
    <polygon fill="#fff" points="37,31 31,27 31,21 37,17"></polygon>
  </svg>
);

const TeamsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-12 h-12" viewBox="0 0 48 48">
    <path fill="#5c6bc0" d="M41.5 13A3.5 3.5 0 1 0 41.5 20 3.5 3.5 0 1 0 41.5 13zM4 40l23 4V4L4 8V40z"></path>
    <path fill="#fff" d="M21 16.27L21 19 17.01 19.18 16.99 31.04 14.01 30.95 14.01 19.29 10 19.45 10 16.94z"></path>
    <path fill="#5c6bc0" d="M36 14c0 2.21-1.79 4-4 4-1.2 0-2.27-.53-3-1.36v-5.28c.73-.83 1.8-1.36 3-1.36C34.21 10 36 11.79 36 14zM38 23v11c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H45v-8H38zM29 20v17c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H36V20H29z"></path>
  </svg>
);

const SlackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" className="w-12 h-12" viewBox="0 0 40 40">
    <path fill="#8cd3df" d="M4.342,21.046c-1.208,0-2.286-0.767-2.683-1.909c-0.25-0.717-0.205-1.488,0.126-2.171 c0.33-0.683,0.907-1.196,1.624-1.446l27.305-9.496c0.303-0.105,0.616-0.159,0.934-0.159c1.207,0,2.286,0.767,2.683,1.909 c0.25,0.717,0.205,1.488-0.126,2.171c-0.33,0.683-0.907,1.196-1.624,1.446L5.275,20.888C4.973,20.993,4.659,21.046,4.342,21.046z"></path>
    <path fill="#5f9099" d="M31.647,6.366L31.647,6.366c0.995,0,1.884,0.632,2.211,1.573c0.424,1.219-0.223,2.556-1.442,2.98 L5.111,20.416c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573c-0.424-1.219,0.223-2.556,1.442-2.98 l27.305-9.496C31.127,6.41,31.386,6.366,31.647,6.366 M31.647,5.366c-0.364,0-0.734,0.06-1.098,0.186L3.245,15.048 c-1.743,0.606-2.665,2.51-2.058,4.253c0.48,1.38,1.773,2.245,3.156,2.245c0.364,0,0.734-0.06,1.098-0.186l27.305-9.496 c1.743-0.606,2.664-2.51,2.058-4.253C34.323,6.231,33.03,5.366,31.647,5.366L31.647,5.366z"></path>
    <path fill="#dd215a" d="M8.353,34.134c-1.207,0-2.286-0.767-2.683-1.909c-0.25-0.717-0.205-1.488,0.126-2.171 c0.33-0.683,0.907-1.196,1.624-1.446l27.305-9.496c0.303-0.105,0.616-0.158,0.934-0.158c1.208,0,2.286,0.767,2.683,1.909 c0.25,0.717,0.205,1.488-0.126,2.171c-0.33,0.683-0.907,1.196-1.624,1.446L9.286,33.975C8.983,34.08,8.67,34.134,8.353,34.134z"></path>
    <path fill="#a81945" d="M35.658,19.454L35.658,19.454c0.995,0,1.884,0.632,2.211,1.573c0.424,1.219-0.223,2.556-1.442,2.98 L9.122,33.503c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573c-0.424-1.219,0.223-2.556,1.442-2.98 l27.305-9.496C35.138,19.498,35.397,19.454,35.658,19.454 M35.658,18.454c-0.364,0-0.734,0.06-1.098,0.186L7.256,28.136 c-1.743,0.606-2.664,2.51-2.058,4.253v0c0.48,1.38,1.773,2.245,3.156,2.245c0.364,0,0.734-0.06,1.098-0.186l27.305-9.496 c1.743-0.606,2.665-2.51,2.058-4.253C38.334,19.318,37.041,18.453,35.658,18.454L35.658,18.454z"></path>
    <path fill="#ebb23d" d="M31.291,34.489c-1.208,0-2.286-0.767-2.683-1.909L19.112,5.276c-0.515-1.48,0.271-3.103,1.75-3.617 C21.165,1.554,21.479,1.5,21.796,1.5c1.208,0,2.287,0.767,2.684,1.909l9.495,27.304c0.25,0.717,0.205,1.488-0.126,2.171 c-0.33,0.683-0.907,1.196-1.624,1.446C31.922,34.436,31.608,34.489,31.291,34.489z"></path>
    <path fill="#b58a2f" d="M21.796,2L21.796,2c0.995,0,1.884,0.632,2.211,1.573l9.496,27.305 c0.424,1.219-0.223,2.556-1.442,2.98c-0.25,0.087-0.508,0.131-0.769,0.131c-0.995,0-1.884-0.632-2.211-1.573L19.584,5.111 c-0.205-0.591-0.169-1.226,0.104-1.789c0.272-0.563,0.748-0.986,1.339-1.192C21.276,2.044,21.535,2,21.796,2 M21.796,1 c-0.364,0-0.734,0.06-1.098,0.186c-1.743,0.606-2.665,2.51-2.058,4.253l9.496,27.305c0.48,1.38,1.773,2.245,3.156,2.245 c0.364,0,0.734-0.06,1.098-0.186c1.743-0.606,2.665-2.51,2.058-4.253L24.952,3.245C24.472,1.865,23.179,1,21.796,1L21.796,1z"></path>
    <path fill="#cd171d" d="M25.865 21.365H31.548V27.046999999999997H25.865z" transform="rotate(-19.178 28.706 24.206)"></path>
    <path fill="#9c1116" d="M30.149,21.226l1.538,4.423l-4.423,1.538l-1.538-4.423L30.149,21.226 M30.765,19.953l-6.312,2.195 l2.195,6.312l6.312-2.195L30.765,19.953L30.765,19.953z"></path>
    <g>
      <path fill="#7f9331" d="M21.372 8.446H27.054000000000002V14.128H21.372z" transform="rotate(-19.178 24.213 11.287)"></path>
      <path fill="#657527" d="M25.656,8.306l1.538,4.423l-4.423,1.538l-1.538-4.423L25.656,8.306 M26.272,7.033L19.96,9.228 l2.195,6.312l6.312-2.195L26.272,7.033L26.272,7.033z"></path>
    </g>
    <g>
      <path fill="#66c1a0" d="M18.204,38.5c-1.208,0-2.287-0.767-2.684-1.909L6.025,9.287C5.775,8.569,5.82,7.798,6.151,7.115 c0.33-0.683,0.907-1.196,1.624-1.446c0.303-0.105,0.616-0.159,0.934-0.159c1.208,0,2.286,0.767,2.683,1.909l9.496,27.305 c0.515,1.48-0.271,3.103-1.75,3.617C18.835,38.446,18.521,38.5,18.204,38.5z"></path>
      <path fill="#4e917a" d="M8.709,6.011L8.709,6.011c0.995,0,1.884,0.632,2.211,1.573l9.496,27.305 c0.424,1.219-0.223,2.556-1.442,2.98C18.724,37.956,18.465,38,18.204,38c-0.995,0-1.884-0.632-2.211-1.573L6.497,9.122 C6.292,8.531,6.329,7.896,6.601,7.333C6.873,6.77,7.349,6.347,7.94,6.142C8.189,6.055,8.448,6.011,8.709,6.011 M8.709,5.011 c-0.364,0-0.734,0.06-1.098,0.186c-1.743,0.606-2.665,2.51-2.058,4.253l9.496,27.305c0.48,1.38,1.773,2.245,3.156,2.245 c0.364,0,0.734-0.06,1.098-0.186c1.743-0.606,2.665-2.51,2.058-4.253L11.864,7.256C11.385,5.876,10.092,5.011,8.709,5.011 L8.709,5.011z"></path>
    </g>
    <g>
      <path fill="#852759" d="M12.945 25.865H18.627000000000002V31.546999999999997H12.945z" transform="rotate(-19.178 15.786 28.706)"></path>
      <path fill="#551537" d="M17.229,25.726l1.538,4.423l-4.423,1.538l-1.538-4.423L17.229,25.726 M17.845,24.453l-6.312,2.195 l2.195,6.312l6.312-2.195L17.845,24.453L17.845,24.453z"></path>
    </g>
    <g>
      <path fill="#3ba08d" d="M8.452 12.946H14.135V18.628H8.452z" transform="rotate(-19.172 11.295 15.789)"></path>
      <path fill="#318575" d="M12.736,12.806l1.538,4.423l-4.423,1.538l-1.538-4.423L12.736,12.806 M13.352,11.533L7.04,13.728 l2.195,6.312l6.312-2.195L13.352,11.533L13.352,11.533z"></path>
    </g>
  </svg>
);

const PROVIDERS = [
  { 
    id: 'meet', 
    name: 'Google Meet',
    icon: GoogleMeetIcon,
    fields: ['client_id', 'client_secret'],
    color: 'blue'
  },
  { 
    id: 'zoom', 
    name: 'Zoom',
    icon: ZoomIcon,
    fields: ['account_id', 'client_id', 'client_secret'],
    color: 'indigo'
  },
  { 
    id: 'teams', 
    name: 'Microsoft Teams',
    icon: TeamsIcon,
    fields: ['tenant_id', 'client_id', 'client_secret'],
    color: 'purple'
  },
  { 
    id: 'slack', 
    name: 'Slack',
    icon: SlackIcon,
    fields: ['bot_token', 'app_id'],
    color: 'green'
  }
];

const VideoConferenceSettings = ({ showToast }) => {
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    provider: null,
    // Google Meet
    client_id: '',
    client_secret: '',
    // Zoom
    account_id: '',
    // Teams
    tenant_id: '',
    // Slack
    bot_token: '',
    app_id: '',
    // Common
    default_duration: 60,
    timezone: 'Europe/Paris',
    is_active: false,
    // Indicators for existing values
    has_client_id: false,
    has_client_secret: false,
    has_account_id: false,
    has_tenant_id: false,
    has_bot_token: false,
    has_app_id: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await appointmentsAPI.getVideoConferenceSettings();
      const data = response.data || {};

      if (data.provider) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          ...data,
          client_id: prevSettings.client_id || '',
          client_secret: prevSettings.client_secret || '',
          account_id: prevSettings.account_id || '',
          tenant_id: prevSettings.tenant_id || '',
          bot_token: prevSettings.bot_token || '',
          app_id: prevSettings.app_id || ''
        }));
        setHasExistingConfig(true);
      } else {
        setHasExistingConfig(false);
      }
    } catch (error) {
      showToast(
        handleApiError(error, t('appointments.video.error.fetch', 'Error loading settings')),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  const handleSave = async () => {
    if (!settings.provider) {
      showToast(t('appointments.video.error.noProvider', 'Veuillez sélectionner un fournisseur'), 'error');
      return;
    }

    // Vérifier que les champs requis sont remplis
    const requiredFields = getProviderFields();
    const missingFields = requiredFields.filter(field => !settings[field] && !settings[`has_${field}`]);
    
    if (missingFields.length > 0 && !hasExistingConfig) {
      showToast(t('appointments.video.error.missingFields', 'Veuillez remplir tous les champs requis'), 'error');
      return;
    }

    try {
      setSaving(true);

      const response = await appointmentsAPI.saveVideoConferenceSettings(settings);
      const data = response.data || {};

      setSettings((prevSettings) => ({
        ...prevSettings,
        ...data,
        client_id: prevSettings.client_id,
        client_secret: prevSettings.client_secret,
        account_id: prevSettings.account_id,
        tenant_id: prevSettings.tenant_id,
        bot_token: prevSettings.bot_token,
        app_id: prevSettings.app_id
      }));
      setHasExistingConfig(true);

      showToast(t('appointments.video.success.save', 'Settings saved successfully'), 'success');
    } catch (error) {
      showToast(handleApiError(error, t('appointments.video.error.save', 'Error saving settings')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await confirm({
      title: t('appointments.video.confirm.deleteTitle', 'Supprimer la configuration'),
      message: t('appointments.video.confirm.delete', 'Êtes-vous sûr de vouloir supprimer cette configuration ?'),
      type: 'danger',
      confirmText: t('common.delete', 'Supprimer'),
      cancelText: t('common.cancel', 'Annuler')
    });
    
    if (!result) {
      return;
    }

    try {
      await appointmentsAPI.deleteVideoConferenceSettings();
      setSettings({
        provider: null,
        client_id: '',
        client_secret: '',
        account_id: '',
        tenant_id: '',
        bot_token: '',
        app_id: '',
        default_duration: 60,
        timezone: 'Europe/Paris',
        is_active: false,
        has_client_id: false,
        has_client_secret: false,
        has_account_id: false,
        has_tenant_id: false,
        has_bot_token: false,
        has_app_id: false
      });
      setHasExistingConfig(false);
      showToast(t('appointments.video.success.delete', 'Configuration deleted'), 'success');
    } catch (error) {
      showToast(
        handleApiError(error, t('appointments.video.error.delete', 'Error deleting configuration')),
        'error'
      );
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getProviderFields = () => {
    const provider = PROVIDERS.find(p => p.id === settings.provider);
    return provider?.fields || [];
  };

  const getFieldLabel = (field) => {
    const labels = {
      client_id: t('appointments.video.fields.clientId', 'Client ID'),
      client_secret: t('appointments.video.fields.clientSecret', 'Client Secret'),
      account_id: t('appointments.video.fields.accountId', 'Account ID'),
      tenant_id: t('appointments.video.fields.tenantId', 'Tenant ID'),
      bot_token: t('appointments.video.fields.botToken', 'Bot Token'),
      app_id: t('appointments.video.fields.appId', 'App ID')
    };
    return labels[field] || field;
  };

  const getFieldPlaceholder = (provider, field) => {
    if (provider === 'meet' && field === 'client_id') {
      return '1234567890-abc.apps.googleusercontent.com';
    }
    if (provider === 'slack' && field === 'bot_token') {
      return 'xoxb-...';
    }
    return t('appointments.video.enterValue', 'Entrez la valeur');
  };

  const handleGoogleAuth = async () => {
    try {
      const response = await appointmentsAPI.getGoogleAuth();
      const authUrl = response.data?.authUrl;
      if (!authUrl) {
        throw new Error('Missing auth URL');
      }
      window.location.href = authUrl;
    } catch (error) {
      showToast(
        handleApiError(error, t('appointments.video.error.googleAuth', 'Google authentication error')),
        'error'
      );
    }
  };

  const handleGoogleCallback = useCallback(async (code) => {
    try {
      await appointmentsAPI.exchangeGoogleToken(code);
      showToast(t('appointments.video.success.googleAuth', 'Google Meet connected successfully'), 'success');
      fetchSettings();
    } catch (error) {
      showToast(
        handleApiError(error, t('appointments.video.error.googleCallback', 'Authentication failed')),
        'error'
      );
    }
  }, [fetchSettings, showToast, t]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchSettings();

      const urlParams = new URLSearchParams(window.location.search);
      const googleCode = urlParams.get('google_code');

      if (googleCode) {
        handleGoogleCallback(googleCode);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [fetchSettings, handleGoogleCallback, user]);

  const handleTestMeeting = async () => {
    try {
      const response = await appointmentsAPI.testVideoMeeting({});
      const result = response.data || {};
      showToast(
        `${t('appointments.video.success.testMeeting', 'Test meeting created successfully')}: ${
          result.meeting?.meetingUrl || '-'
        }`,
        'success'
      );
    } catch (error) {
      showToast(handleApiError(error, t('appointments.video.error.testMeeting', 'Test failed')), 'error');
    }
  };

  // Vérifier si l'utilisateur est admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="p-4 bg-red-50 rounded-full">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('appointments.video.adminOnlyTitle', 'Accès administrateur requis')}
        </h3>
        <p className="text-gray-600 text-center max-w-md">
          {t('appointments.video.adminOnlyMessage', 'Seuls les administrateurs peuvent configurer les paramètres de visioconférence.')}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Avertissement admin */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 shadow-sm"
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">
              {t('appointments.video.adminOnly', 'Configuration administrateur')}
            </h3>
            <p className="text-sm text-amber-700">
              {t('appointments.video.adminOnlyDesc', 'Ces paramètres s\'appliquent à tous les utilisateurs du workspace')}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-gray-200"
      >
        <div className="p-8">
          <div className="mb-8">
            <div className="flex items-center mb-3">
              <div className="p-3 bg-indigo-50 rounded-xl mr-4">
                <Video className="h-7 w-7 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('appointments.video.title', 'Configuration de la visioconférence')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('appointments.video.description', 'Configurez votre outil de réunion pour les rendez-vous en ligne')}
                </p>
              </div>
            </div>
          </div>

          {/* Sélection du fournisseur */}
          <div className="mb-8">
            <label className="block text-base font-semibold text-gray-800 mb-4">
              {t('appointments.video.provider', 'Fournisseur de visioconférence')}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PROVIDERS.map((provider) => {
              const isSelected = settings.provider === provider.id;
              
              return (
                <motion.button
                  key={provider.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSettings({ ...settings, provider: provider.id })}
                  className={`
                    relative rounded-xl border-2 p-5 text-center transition-all duration-200
                    ${isSelected 
                      ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }
                  `}
                >
                  <div className="mb-3 flex justify-center">
                    <provider.icon />
                  </div>
                  <div className="text-sm font-semibold text-gray-800">{provider.name}</div>
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1"
                    >
                      <div className="bg-indigo-500 rounded-full p-1">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    </motion.div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Configuration spécifique au fournisseur */}
        {settings.provider && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Champs API */}
            {getProviderFields().length > 0 && (
              <div className="border-t border-gray-100 pt-6 mt-6">
                <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="p-2 bg-gray-100 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </div>
                  {t('appointments.video.apiConfig', 'Configuration API')}
                </h3>
                
                {/* Note informative pour les champs configurés */}
                {hasExistingConfig && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <AlertTriangle className="h-4 w-4 inline mr-2" />
                      {t('appointments.video.configuredNote', 'Pour des raisons de sécurité, les valeurs sensibles ne sont pas affichées. Saisissez de nouvelles valeurs uniquement si vous souhaitez les modifier.')}
                    </p>
                  </div>
                )}
                
                <div className="space-y-4">
                  {getProviderFields().map((field) => {
                    const isSecretField = field.includes('secret') || field.includes('token');
                    const fieldLabel = getFieldLabel(field);
                    const fieldPlaceholder = getFieldPlaceholder(settings.provider, field);
                    
                    return (
                      <motion.div
                        key={field}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * getProviderFields().indexOf(field) }}
                      >
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {fieldLabel}
                        </label>
                        <div className="relative">
                          <input
                            type={isSecretField && !showSecrets[field] ? 'password' : 'text'}
                            value={settings[field] || ''}
                            onChange={(e) => setSettings({ ...settings, [field]: e.target.value })}
                            placeholder={
                              hasExistingConfig && settings[`has_${field}`]
                                ? t('appointments.video.configured', '(Configuré)')
                                : fieldPlaceholder
                            }
                            className="block w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 pr-12"
                          />
                          {isSecretField && (
                            <button
                              type="button"
                              onClick={() => toggleShowSecret(field)}
                              className="absolute inset-y-0 right-0 px-3 flex items-center hover:bg-gray-100 rounded-r-lg transition-colors"
                              aria-label={showSecrets[field] ? t('auth.password.hide') : t('auth.password.show')}
                            >
                              {showSecrets[field] ? (
                                <EyeOff className="h-5 w-5 text-gray-500" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-500" />
                              )}
                            </button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {/* Google OAuth Button */}
                  {settings.provider === 'meet' && settings.has_client_id && settings.has_client_secret && !settings.has_account_id && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4"
                    >
                      <button
                        type="button"
                        onClick={handleGoogleAuth}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        {t('appointments.video.authorizeGoogle', 'Autoriser Google Meet')}
                      </button>
                    </motion.div>
                  )}
                  
                  {/* Google OAuth Success */}
                  {settings.provider === 'meet' && settings.has_account_id && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4"
                    >
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <p className="text-green-700 text-sm font-medium">
                          {t('appointments.video.googleAuthorized', 'Google Meet est autorisé et prêt à être utilisé')}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            )}

            {/* Paramètres généraux */}
            <div className="border-t border-gray-100 pt-6 mt-6">
              <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                {t('appointments.video.generalSettings', 'Paramètres généraux')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('appointments.video.defaultDuration', 'Durée par défaut')}
                  </label>
                  <select
                    value={settings.default_duration}
                    onChange={(e) => setSettings({ ...settings, default_duration: parseInt(e.target.value) })}
                    className="block w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                  >
                    <option value={30}>30 {t('common.minutes', 'minutes')}</option>
                    <option value={45}>45 {t('common.minutes', 'minutes')}</option>
                    <option value={60}>60 {t('common.minutes', 'minutes')}</option>
                    <option value={90}>90 {t('common.minutes', 'minutes')}</option>
                    <option value={120}>120 {t('common.minutes', 'minutes')}</option>
                  </select>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('appointments.video.timezone', 'Fuseau horaire')}
                  </label>
                  <TimezoneSelector 
                    value={settings.timezone} 
                    onChange={(tz) => setSettings({ ...settings, timezone: tz })}
                    showLabel={false}
                    className="w-full"
                  />
                </motion.div>
              </div>
            </div>

            {/* Activation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="border-t border-gray-100 pt-6 mt-6"
            >
              <label className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.is_active}
                  onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {t('appointments.video.active', 'Activer la visioconférence pour les rendez-vous')}
                </span>
              </label>
            </motion.div>
          </motion.div>
        )}

          {/* Actions */}
          <div className="mt-8 flex justify-between items-center border-t border-gray-100 pt-6">
            <div className="flex space-x-3">
              {hasExistingConfig && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDelete}
                    className="inline-flex items-center px-5 py-2.5 border border-red-200 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-all duration-200"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.delete', 'Supprimer')}
                  </motion.button>
                  
                  {settings.is_active && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleTestMeeting}
                      className="inline-flex items-center px-5 py-2.5 border border-blue-200 text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                    >
                      <Video className="h-4 w-4 mr-2" />
                      {t('appointments.video.testMeeting', 'Tester la création')}
                    </motion.button>
                  )}
                </>
              )}
            </div>
            <motion.button
              whileHover={{ scale: saving || !settings.provider ? 1 : 1.02 }}
              whileTap={{ scale: saving || !settings.provider ? 1 : 0.98 }}
              onClick={handleSave}
              disabled={saving || !settings.provider}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-lg shadow-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('common.saving', 'Enregistrement...')}
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {hasExistingConfig ? t('common.edit', 'Modifier') : t('common.save', 'Enregistrer')}
                </>
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Instructions */}
      {settings.provider && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-base font-semibold text-blue-900 mb-3">
                {t('appointments.video.instructions', 'Instructions de configuration')}
              </h3>
              <div className="text-sm text-blue-800 space-y-2">
            {settings.provider === 'meet' && (
                  <>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">1.</span>
                      {t('appointments.video.meetInstructions1', 'Accédez à Google Cloud Console')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">2.</span>
                      {t('appointments.video.meetInstructions2', 'Créez un nouveau projet ou sélectionnez-en un existant')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">3.</span>
                      {t('appointments.video.meetInstructions3', 'Activez Google Calendar API et Google Meet API')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">4.</span>
                      {t('appointments.video.meetInstructions4', 'Créez des identifiants OAuth 2.0')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">5.</span>
                      {t('appointments.video.meetInstructions5', 'Ajoutez les URI de redirection autorisés')}
                    </p>
                  </>
            )}
            {settings.provider === 'zoom' && (
                  <>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">1.</span>
                      {t('appointments.video.zoomInstructions1', 'Connectez-vous à votre compte Zoom Marketplace')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">2.</span>
                      {t('appointments.video.zoomInstructions2', 'Créez une app OAuth Server-to-Server')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">3.</span>
                      {t('appointments.video.zoomInstructions3', 'Ajoutez les scopes meeting:write et meeting:read')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">4.</span>
                      {t('appointments.video.zoomInstructions4', 'Copiez l\'Account ID, Client ID et Client Secret')}
                    </p>
                  </>
            )}
            {settings.provider === 'teams' && (
                  <>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">1.</span>
                      {t('appointments.video.teamsInstructions1', 'Accédez à Azure Active Directory')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">2.</span>
                      {t('appointments.video.teamsInstructions2', 'Enregistrez une nouvelle application')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">3.</span>
                      {t('appointments.video.teamsInstructions3', 'Configurez les permissions: OnlineMeetings.ReadWrite')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">4.</span>
                      {t('appointments.video.teamsInstructions4', 'Créez un secret client')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">5.</span>
                      {t('appointments.video.teamsInstructions5', 'Notez le Tenant ID et Client ID')}
                    </p>
                  </>
            )}
            {settings.provider === 'slack' && (
                  <>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">1.</span>
                      {t('appointments.video.slackInstructions1', 'Créez une app Slack')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">2.</span>
                      {t('appointments.video.slackInstructions2', 'Ajoutez les scopes: calls:write, calls:read')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">3.</span>
                      {t('appointments.video.slackInstructions3', 'Installez l\'app dans votre workspace')}
                    </p>
                    <p className="flex items-start">
                      <span className="font-semibold mr-2">4.</span>
                      {t('appointments.video.slackInstructions4', 'Copiez le Bot User OAuth Token')}
                    </p>
                  </>
            )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default VideoConferenceSettings;
